/**
 * Validación pura de la configuración de un hábito. Devuelve códigos de error
 * estables que la interfaz traduce.
 */
import { isValidLocalDate } from './dates.js';
import type { HabitDef, HabitVersion, Schedule } from './types.js';

export type ValidationCode =
  | 'invalid_start_date'
  | 'invalid_end_date'
  | 'end_before_start'
  | 'no_versions'
  | 'invalid_target'
  | 'invalid_schedule'
  | 'empty_schedule'
  | 'quit_schedule_not_allowed'
  | 'invalid_due_date'
  | 'average_not_allowed';

function validSchedule(s: Schedule): ValidationCode | null {
  switch (s.type) {
    case 'daily':
      return null;
    case 'weekdays':
      if (!Array.isArray(s.days) || s.days.some((d) => !Number.isInteger(d) || d < 1 || d > 7)) return 'invalid_schedule';
      if (s.everyWeeks !== undefined && (!Number.isInteger(s.everyWeeks) || s.everyWeeks < 1 || s.everyWeeks > 12)) return 'invalid_schedule';
      return s.days.length === 0 ? 'empty_schedule' : null;
    case 'yearly':
      return null;
    case 'monthdays':
      if (!Array.isArray(s.days) || s.days.some((d) => !Number.isInteger(d) || d < 1 || d > 31)) return 'invalid_schedule';
      return s.days.length === 0 && !s.lastDay ? 'empty_schedule' : null;
    case 'interval_days':
      return Number.isInteger(s.every) && s.every >= 1 && s.every <= 365 ? null : 'invalid_schedule';
    case 'weekly_quota':
    case 'monthly_quota': {
      if (s.mode !== 'times' && s.mode !== 'amount') return 'invalid_schedule';
      if (s.mode === 'times') {
        const max = s.type === 'weekly_quota' ? 7 : 31;
        if (!Number.isInteger(s.times) || (s.times ?? 0) < 1 || (s.times ?? 0) > max) return 'invalid_schedule';
      }
      return null;
    }
    default:
      return 'invalid_schedule';
  }
}

export function validateVersion(kind: HabitDef['kind'], v: HabitVersion): ValidationCode[] {
  const errors: ValidationCode[] = [];
  if (!isValidLocalDate(v.effectiveFrom)) errors.push('invalid_start_date');
  if (!Number.isFinite(v.target) || v.target > 1_000_000) errors.push('invalid_target');
  else if (kind === 'build' && v.target <= 0) errors.push('invalid_target');
  else if (kind === 'quit' && v.target < 0) errors.push('invalid_target');
  const s = validSchedule(v.schedule);
  if (s) errors.push(s);
  if (kind === 'quit' && (v.schedule.type === 'weekly_quota' || v.schedule.type === 'monthly_quota') && v.schedule.mode === 'times') {
    errors.push('quit_schedule_not_allowed');
  }
  if (v.average != null) {
    const quota = v.schedule.type === 'weekly_quota' || v.schedule.type === 'monthly_quota';
    if (kind !== 'build' || v.goalType !== 'numeric' || quota || !Number.isInteger(v.average) || v.average < 2 || v.average > 30) {
      errors.push('average_not_allowed');
    }
  }
  return errors;
}

export function validateHabit(h: HabitDef): ValidationCode[] {
  const errors: ValidationCode[] = [];
  if (!isValidLocalDate(h.startDate)) errors.push('invalid_start_date');
  if (h.endDate != null) {
    if (!isValidLocalDate(h.endDate)) errors.push('invalid_end_date');
    else if (h.endDate < h.startDate) errors.push('end_before_start');
  }
  if (h.kind === 'task') {
    if (h.dueDate != null && !isValidLocalDate(h.dueDate)) errors.push('invalid_due_date');
    return errors;
  }
  if (h.versions.length === 0) errors.push('no_versions');
  for (const v of h.versions) errors.push(...validateVersion(h.kind, v));
  return [...new Set(errors)];
}
